import { Icons } from '@commenda/kakashi';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@commenda/utils';
import { ToolCallHelper, type ToolCallPhase } from './ToolCallHelper';

const PHASE_BY_STATE: Record<string, ToolCallPhase> = {
  'input-streaming': 'running',
  'input-available': 'running',
  'approval-requested': 'running',
  'approval-responded': 'running',
  'output-available': 'done',
  'output-error': 'failed',
  'output-denied': 'failed',
};

interface ToolCallCardProps {
  toolName: string;
  state: string;
  input: unknown;
  output: unknown;
  errorText: string | undefined;
}

/** Tool inputs/outputs often arrive as a JSON *string* — parse it so it nests as real JSON in the
 * payload instead of being double-escaped ("{\n \"total\": 0 …}"). Non-JSON strings pass through. */
function parseMaybeJson(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

/** One tool call, styled to the chat Figma: a compact row (tool glyph + muted past-tense label +
 * chevron) that expands to a bordered card showing the raw `tool_name` and the request/response
 * JSON with a copy button. Failed calls read "Failed to <action>" with a red lead. */
export function ToolCallCard({
  toolName,
  state,
  input,
  output,
  errorText,
}: ToolCallCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyResetRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(copyResetRef.current), []);

  const phase = PHASE_BY_STATE[state] ?? 'done';
  const isFailed = phase === 'failed';

  // The expanded card mirrors the Figma: request (input) + response (output, or the error).
  const payload = JSON.stringify(
    {
      request: parseMaybeJson(input) ?? null,
      response: parseMaybeJson(errorText ?? output),
    },
    null,
    2
  );

  const handleCopy = () => {
    navigator.clipboard
      .writeText(payload)
      .then(() => {
        setCopied(true);
        clearTimeout(copyResetRef.current);
        copyResetRef.current = setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  };

  return (
    <div className="flex w-full flex-col gap-md">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        className="flex w-fit items-center gap-sm border-none bg-transparent p-0 text-left"
      >
        <Icons.Tool01
          className="size-3 shrink-0 text-fg-quaternary"
          aria-hidden="true"
        />
        <span className="txt-extra-small-medium">
          {isFailed ? (
            <>
              <span className="text-text-error">Failed to</span>{' '}
              <span className="text-text-quaternary">
                {ToolCallHelper.getFailureLabel(toolName)}
              </span>
            </>
          ) : (
            <span className="text-text-quaternary">
              {ToolCallHelper.getActivityLabel(toolName, phase)}
            </span>
          )}
        </span>
        {isOpen ? (
          <Icons.ChevronDown
            className="size-3 shrink-0 text-fg-quaternary"
            aria-hidden="true"
          />
        ) : (
          <Icons.ChevronRight
            className="size-3 shrink-0 text-fg-quaternary"
            aria-hidden="true"
          />
        )}
      </button>

      {isOpen && (
        // Indent the card under the label (glyph size-3 + gap-sm = 18px).
        <div className="pl-[18px]">
          <div className="w-full overflow-hidden rounded-md border-[0.5px] border-border-primary bg-bg-primary">
            <div className="px-lg py-md">
              <span className="font-mono text-[10px] text-text-primary">
                {ToolCallHelper.getDisplayName(toolName)}
              </span>
            </div>
            <div className="border-t border-border-primary" />
            <div className="flex items-start gap-sm px-lg py-md">
              <pre className="min-w-0 flex-1 overflow-x-auto whitespace-pre-wrap font-mono text-[10px] leading-relaxed text-text-secondary">
                {payload}
              </pre>
              <button
                type="button"
                onClick={handleCopy}
                aria-label="Copy"
                className={cn(
                  'shrink-0 cursor-pointer border-none bg-transparent p-0 transition-colors',
                  copied
                    ? 'text-fg-brand'
                    : 'text-fg-quaternary hover:text-text-primary'
                )}
              >
                {copied ? (
                  <Icons.Check className="size-3" aria-hidden="true" />
                ) : (
                  <Icons.Copy06 className="size-3" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
